<template>
    <article>
        <p>
            Kubernetes operators for databases have become a real alternative to cloud DBaaS (Database-as-a-Service) offerings.
            Operators handle most “Day 2” database operations such as high availability, backups, and disaster recovery.
            Moreover, running databases in Kubernetes is cost-effective and eliminates vendor lock-in.
        </p>

        <p>
            Despite the high level of automation, no operator can guarantee 100% availability or a flawless performance of your database,
            so it is very important to get visibility into each database cluster.
            Coroot can help you with that.
            For Postgres clusters managed by the <a href="https://github.com/zalando/postgres-operator" target="_blank">Postgres Operator</a>,
            it not only highlights <router-link :to="{name: 'blog', params: {name: 'pg-agent'}}">performance issues</router-link> of a
            particular instance but also detects cluster-level issues.
            Thanks to the <router-link :to="{name: 'blog', params: {name: 'building-a-service-map-using-ebpf'}}">eBPF-based connection metrics</router-link>,
            Coroot can track all communications between cluster components such as
            <a href="https://github.com/zalando/patroni" target="_blank">Patroni</a>, Postgres, Pgbouncer, and the Kubernetes API server.
        </p>

        <p>
            To test Coroot, we reproduce real failure scenarios in our lab and record their telemetry. This allows us to play back any scenario second by second.
            In this post, I'll use such recordings to show you how the Postgres operator and its underlying components handle various failures.
        </p>

        <h2>The lab</h2>

        <ul>
            <li>A Kubernetes cluster: 1 master and 3 worker nodes</li>
            <li><a href="https://github.com/zalando/postgres-operator" target="_blank">Postgres operator</a> v1.8.2</li>
            <li><a href="https://chaos-mesh.org/" target="_blank">Chaos Mesh</a> for failure injection</li>
            <li>A test application (<var>app</var>) that runs queries on both the primary Postgres instance and its replicas.</li>
            <li>
                Observability:
                <a href="https://github.com/prometheus/prometheus" target="_blank">Prometheus</a>,
                <a href="https://github.com/kubernetes/kube-state-metrics">kube-state-metrics</a>,
                <a href="https://github.com/coroot/coroot-node-agent" target="_blank">coroot-node-agent</a>,
                <a href="https://github.com/coroot/coroot-pg-agent" target="_blank">coroot-pg-agent</a>
            </li>
            <li>A Postgres cluster of 3 instances:</li>

        </ul>

        <pre v-hl.yaml>
apiVersion: "acid.zalan.do/v1"
kind: postgresql
metadata:
  name: db-main
  namespace: default
spec:
  teamId: db
  volume:
    size: 10Gi
  numberOfInstances: 3
  users:
    coroot: [superuser] # TODO: grant pg_monitor to coroot;
    app: []
  databases:
    app: app
  postgresql:
    version: "14"
  sidecars:
  - name: coroot-pg-agent
    image: ghcr.io/coroot/coroot-pg-agent:1.1.0
    env:
    - name: LISTEN
      value: ":9091"
    - name: PG_SCRAPE_INTERVAL
      value: "1s"
    - name: PGPASSWORD
      valueFrom: {secretKeyRef: {name: coroot.db-main.credentials.postgresql.acid.zalan.do, key: password}}
    - name: DSN
      value: "host=127.0.0.1 port=5432 user=coroot password=$(PGPASSWORD) dbname=postgres connect_timeout=1 statement_timeout=10000"
  podAnnotations:
    prometheus.io/scrape: 'true'
    prometheus.io/port: '9091'
        </pre>

        <p>
            In terms of Kubernetes, the cluster is a StatefulSet of 3 pods. Postgres within each pod is managed by Patroni.
            Patroni turns a bunch of Postgres instances into an HA (High Availability) cluster, it's responsible for replication management and failovers/switchovers.
            When Patroni is deployed by the Postgres operator, it uses the Kubernetes control plane (<var>kube-apiserver</var>)
            as the DCS (Distributed Config Store) for leader election and storing the cluster state.
        </p>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/pg_operator_chaos_testing/cluster.png" />
        </v-card>

        <p>
            The operator creates two Kubernetes services for incoming client connections to each cluster:
            <var>&lt;cluster-name&gt;</var> pointing to the primary
            and <var>&lt;cluster-name&gt;-repl</var> pointing to the replicas.
        </p>

        <pre v-hl.sh>
$ kubectl get services -l cluster-name=db-main -l spilo-role
NAME             TYPE        CLUSTER-IP     EXTERNAL-IP   PORT(S)    AGE
db-main          ClusterIP   10.96.17.243   &lt;none&gt;        5432/TCP   6d18h
db-main-repl     ClusterIP   10.99.79.27    &lt;none&gt;        5432/TCP   6d18h

$ kubectl get endpoints -l cluster-name=db-main -l spilo-role
NAME           ENDPOINTS                           AGE
db-main        10.244.3.64:5432                    6d18h
db-main-repl   10.244.1.92:5432,10.244.2.82:5432   6d18h
        </pre>

        <h2>A replica failure</h2>

        <p>
            Let's start with the simplest failure scenario when a replica becomes unavailable.
            In such scenarios, I prefer to use <a href="https://chaos-mesh.org/docs/simulate-network-chaos-on-kubernetes/" target="_blank">network partitioning</a> because
            this type of failure is harder to detect than <a href="https://chaos-mesh.org/docs/simulate-pod-chaos-on-kubernetes/" target="_blank">Pod faults</a>.
        </p>

        <v-img eager max-width="80%" class="ma-auto my-6" src="/static/img/blog/pg_operator_chaos_testing/replica_failure.png" />

        <pre v-hl.yaml>
kind: NetworkChaos
apiVersion: chaos-mesh.org/v1alpha1
metadata:
  name: replica-net-partition
spec:
  selector:
    labelSelectors:
      application: spilo
      spilo-role: replica
  mode: one
  action: partition
  direction: both
        </pre>

        <p>
            The operator uses the <a href="https://github.com/zalando/postgres-operator/blob/v1.8.2/pkg/cluster/k8sres.go#L1086" target="_blank">hardcoded</a>
            readiness probe configuration for the database pods.
            The probe makes a request to <a href="https://patroni.readthedocs.io/en/latest/rest_api.html#health-check-endpoints" target="_blank"><var>/readiness</var></a>
            every 10 seconds and marks the pod as <var>Unready</var> after 3 unsuccessful attempts.
            Thus, we can expect that Kubernetes will stop forwarding connections to the failed pod within 30 seconds.
        </p>

        <p>Let's check it out!</p>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/pg_operator_chaos_testing/replica_failure_coroot.png" />
        </v-card>

        <p>
            There were errors due to the inability to connect to the fenced replica,
            but these errors disappeared after 30 seconds when the replica was marked as <var>Unready</var>.
        </p>

        <h2>Failover</h2>

        <p>
            Now let's see how the operator handles the failure of the primary
            Postgres instance using Patroni.
        </p>

        <v-img eager max-width="80%" class="ma-auto my-6" src="/static/img/blog/pg_operator_chaos_testing/primary_failure.png" />

        <p>
            By default Patroni acquires the leader lock in the DCS (Distributed Configuration Store) with
            the <a href="https://patroni.readthedocs.io/en/latest/SETTINGS.html#dynamic-configuration-settings" target="_blank">TTL</a>
            (Time-To-Live) at 30 seconds. In other words, the leader must refresh its lock every 30 seconds.
            If there is no network connectivity between the primary and the DCS (<var>kube-apiserver</var> in this case),
            the primary will not be able to refresh its lock.
            Therefore, the failover process should be started within this time interval.
        </p>

        <p>
            The following <var>NetworkChaos</var> manifest isolates the primary Postgres pod from the rest of the network:
        </p>

        <pre v-hl.yaml>
kind: NetworkChaos
apiVersion: chaos-mesh.org/v1alpha1
metadata:
  name: primary-net-partition
spec:
  selector:
    labelSelectors:
      application: spilo
      spilo-role: master
  mode: all
  action: partition
  direction: both
        </pre>

        <p>Let's apply it and see the automatic failover mechanism in action!</p>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/pg_operator_chaos_testing/primary_failure_coroot.png" />
        </v-card>

        <p>
            Wow, it works exactly as expected! The failover process <v-icon small>mdi-database-sync-outline</v-icon> was initiated
            23 seconds after the primary Postgres had become unavailable <v-icon small>mdi-alert-octagon-outline</v-icon>.
            From the app's perspective, the outage lasted only 27 seconds.
        </p>

        <p>
            Another thing worth noting is that the new primary instance performed slowly for a while after being promoted
            because several processes were causing high disk and CPU utilization:
        </p>

        <ul>
            <li><var>CHECKPOINT</var> initiated by Patroni on the primary after a switchover/failover</li>
            <li><var>ANALYZE</var> updating table statistics</li>
            <li>warming up of the buffer and page caches</li>
        </ul>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/pg_operator_chaos_testing/new_primary.png" />
        </v-card>

        <h2>Replication lag</h2>

        <p>
            Read replicas are useful for scaling out read load. However, when using asynchronous replication,
            it takes some time for the new data to reach replicas. So, the application that executes queries on replicas
            must be tolerant to a little lag.
            On the other hand, if data stops replicating, it can affect the users of the application.
        </p>

        <v-img eager max-width="80%" class="ma-auto my-6" src="/static/img/blog/pg_operator_chaos_testing/replica_to_primary_loss.png" />

        <p>
            To illustrate such a scenario, we implemented a freshness check in our test application: if the delay in the data fetched from a replica and the
            primary exceeds 10 seconds, the app reports an error.
        </p>

        <p>
            Our next step is making the replication lag grow.
            Roughly speaking, streaming replication in Postgres has 3 stages:
        </p>

        <ul>
            <li>
                Transferring WAL (Write-Ahead Logging) records to the replica through the network (this can be affected by a network attack)
            </li>
            <li>
                Writing WAL records to the disk on the replica (this can be affected by an I/O injection, but it might cause many side effects)
            </li>
            <li>
                Replaying WAL records on the replica (this can be affected by executing a long query on the replica,
                but this query will be terminated after <var>max_standby_streaming_delay</var>)
            </li>
        </ul>

        <p>
            So, a network attack seems like the simplest way to cause high replication lag.
            Let's simulate an 80% packet loss between the primary and a replica.
        </p>


        <pre v-hl.yaml>
kind: NetworkChaos
apiVersion: chaos-mesh.org/v1alpha1
metadata:
  name: replica-to-primary-packet-loss
spec:
  selector:
    labelSelectors:
      spilo-role: replica
  mode: one
  action: loss
  loss:
    loss: '80'
  direction: both
  target:
    selector:
      labelSelectors:
        spilo-role: master
    mode: all
        </pre>

        <p>
            As you can see in the manifest above, Chaos Mesh can simulate a network fault between specific Kubernetes pods.
            It's quite useful when we want to simulate partial network outages.
        </p>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/pg_operator_chaos_testing/replication_lag.png" />
        </v-card>

        <p>
            Unfortunately, the operator cannot handle such situations yet.
            In Patroni, there is the <a href="https://patroni.readthedocs.io/en/latest/rest_api.html#health-check-endpoints" target="_blank">
            <var>/replica?lag=&lt;max-lag&gt;</var></a> health check endpoint which verifies that the
            lag of a particular replica is below the specified <var>&lt;max-lag&gt;</var>.
            However, it cannot be used as the readiness probe in the StatefulSet because it checks both the primary and the replicas.
            To address this a compound health check like <var>/readiness?lag=&lt;max-lag&gt;</var> could be implemented in Patroni
            to check the lag only when the instance is running as the replica.
        </p>

        <h2>Connection pooler</h2>

        <p>Now, let's add a connection pooler (<a href="https://www.pgbouncer.org/" target="_blank">PgBouncer</a>) for the primary:</p>

        <pre v-hl.yaml>
spec:
  enableConnectionPooler: true
  connectionPooler:
    numberOfInstances: 2
    mode: "session"
        </pre>

        <p>
            After applying the changes, the operator created the <var>db-main-pooler</var> Deployment and the relevant Kubernetes service.
        </p>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/pg_operator_chaos_testing/connection_pooler.png" />
        </v-card>

        <p>
            The readiness probe of the pooler pods is <var>tcp-socket :5432 delay=0s timeout=1s period=10s #success=1 #failure=3</var>.
            So, if we completely isolate a pod, the failure will be handled by Kubernetes within 30 seconds.
            Let's perform a more interesting experiment in which a pooler pod loses connectivity with Postgres but is still reachable by its clients.
        </p>

        <v-img eager max-width="80%" class="ma-auto my-6" src="/static/img/blog/pg_operator_chaos_testing/pooler.png" />

        <pre v-hl.yaml>
kind: NetworkChaos
apiVersion: chaos-mesh.org/v1alpha1
metadata:
  name: pooler-net-partition
spec:
  selector:
    labelSelectors:
      connection-pooler: db-main-pooler
  mode: one
  action: partition
  direction: both
  target:
    selector:
      labelSelectors:
        application: spilo
        spilo-role: replica
    mode: all
        </pre>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/pg_operator_chaos_testing/pooler_failure.png" />
        </v-card>

        <p>
            As expected, such a failure is not handled automatically.
            However, it is fair to say that the probability of such an issue occurring is extremely low.
        </p>

        <h2>Bonus track: a major Postgres upgrade</h2>

        <p>
            The operator can handle major Postgres upgrades automatically.
            Depending on the <var>major_version_upgrade_mode</var> option, the operator can upgrade a
            particular cluster (<var>manual</var>) or all clusters (<var>full</var>).
        </p>

        <p>
            Below are the main steps the operator performs to upgrade a cluster:
        </p>

        <ul>
            <li>changes the desired Postgres version in the database pods:
                <ul>
                    <li>performs a rolling update of the replicas <span class="downtime">[replicas downtime]</span></li>
                    <li>initiates a switchover (planned failover) of the primary to one of the replicas <span class="downtime">[downtime]</span></li>
                </ul>
            </li>

            <li>
                executes the <var>/scripts/inplace_upgrade.py</var> script
                (a part of <a href="https://github.com/zalando/spilo/blob/master/postgres-appliance/major_upgrade/inplace_upgrade.py" target="_blank">Spilo</a>) within the primary pod:
                <ul>
                    <li>checks the cluster health and runs sanity checks</li>
                    <li><a href="https://github.com/zalando/patroni/blob/v2.1.4/docs/pause.rst" target="_blank">pauses</a> Patroni for the cluster to disable automatic failover</li>
                    <li>shuts down the primary Postgres <span class="downtime">[downtime]</span></li>
                    <li>runs <var>CHECKPOINT</var> on the replicas <span class="downtime">[downtime]</span></li>
                    <li>executes <var>pg_upgrade</var> on the primary to convert <var>PGDATA</var> to the desired Postgres version <span class="downtime">[downtime]</span></li>
                    <li>shuts down the replicas <span class="downtime">[downtime]</span></li>
                    <li>rsyncs <var>PGDATA</var> to the replicas <span class="downtime">[downtime]</span></li>
                    <li>starts up the primary and replicas</li>
                    <li>resumes Patroni for the cluster</li>
                    <li>executes <var>ANALYZE</var></li>
                </ul>
            </li>
        </ul>

        <p>
            Let's upgrade the cluster to Postgres 14 and see how long it is unavailable for:
        </p>

        <pre v-hl.sh>
kubectl patch postgresql db-main --type='json' --patch='[{"op": "replace", "path": "/spec/postgresql/version", "value": "14"}]'
        </pre>

        <v-card class="text-center pa-4 mb-4">
            <v-img eager class="ma-auto" src="/static/img/blog/pg_operator_chaos_testing/major_upgrade.png" />
        </v-card>

        <p>
            The initial rolling update caused 10 seconds of downtime due to the switchover.
            The downtime caused by the upgrade itself lasted 22 seconds.
        </p>

        <h2>Conclusion</h2>

        <p>
            Chaos engineering is fun!
            It not only allows you to test how well a service is able to tolerate failures
            but also to find out how observable your infrastructure is.
        </p>

        <p>
            The conducted experiments verify that the Postgres operator and Patroni handle most
            failures perfectly. I hope this gives you more confidence to use them in production.
        </p>

        <p>
            If you want to improve the observability of your infrastructure, give Coroot <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'getting-started', item: 'installation'}}">a chance</router-link>!
        </p>
    </article>
</template>

<script>
export default {
}
</script>

<style scoped>
.downtime {
    color: #FF5E67;
    font-weight: 400;
}
</style>
